import React, {
	useMemo,
	useContext,
	useRef,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import { OpenTokContext } from '../../contexts/opentok/opentok-context';
import type { Invitation } from '../../stores/types/invitations';
import { RESTRICTED_USER_STATES } from '../../stores/reducers/session';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { GreenroomState as GS } from '../../stores/types/types';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { VideoStream } from '../types/videos';
import RestrictedUserThumbnail from './restricted-user-thumbnail';
import VideoFeedFakeThumbnail from './video-feed-fake-thumbnail';
import VideoFeedThumbnail from './video-feed-thumbnail';
import SipConnector from './sip-connector';
import { setMetaMenuRoute, setMetaMenuState } from '../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../stores/types/tabs';
import useEditProfileMask from '../../hooks/use-edit-profile-mask';
import type { DragItem } from '../../stores/types/user-interface';
import type { OnsiteLocation, SrtStream } from '../../stores/types/video';
import { SrtStreamStatus } from '../../stores/types/video';
import SrtStreamThumbnail from '../main-video/srt-stream/srt-stream-thumbnail';
import { ThumbnailLocation } from '../../types/enums';
import OnsiteLocationThumbnail from '../main-video/onsite-location/onsite-location-thumbnail';
import { selectSrtStreams } from '../../stores/selectors/video';

interface Props {
	className?: string;
	socket?: Socket | null;
}

export default function FeedsTab(props: Props) {
	const dispatch = useDispatch();

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const restrictedUserState = useSelector((s: GS) => s.SessionReducer.restrictedUserState);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const presenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const { standaloneSrtStreams, onsiteSrtStreams } = useSelector(selectSrtStreams);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const onsiteLocationStreams = useSelector((s: GS) => s.VideoReducer.onsiteLocationStreams);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const {
		use_sip: useSip,
		use_srt_streams: useSrtStreams,
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const {
		className,
		socket,
	} = props;

	const { opentok } = useContext(OpenTokContext);

	const feedsRef = useRef<HTMLDivElement | null>(null);

	const invitePresenters = () => {
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.SHARING));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
	};

	const preconnectVideo = useMemo(() => {
		if (!opentok?.session?.connection && restrictedUserState === RESTRICTED_USER_STATES.NONE) {
			return presenterVideos.find((vid: VideoStream) => vid.self || vid.local);
		}
		return undefined;
	}, [opentok, restrictedUserState, presenterVideos]);

	const restrictedUsers = useMemo(() => connectedUsers.filter(
		(u: ConnectedUser) => u.in_room
		&& u.restrictedUserState
		&& u.restrictedUserState > 2 // cam only and mic only restricted users show as presenter feeds
		&& !u.watcher_only,
	), [connectedUsers]);

	const invitedUsersNotInRoom = useMemo(() => {
		if (roomState === ROOM_STATES.EDITING && host) {
			// create fake feed for all invites that aren't in the room
			return invitations.filter(
				(invitation: Invitation) => !connectedUsers.some(
					(user: any) => user?.in_room && (
						(user.gr_user && user.gr_user === invitation.profile.gr_user)
						|| (user.email && user.email === invitation.profile.email)
					),
				) && !invitation.watcher_only,
			);
		}
		return [];
	}, [connectedUsers, invitations, host]);

	const renderedPresenterVideos = useMemo(() => {
		const newVideos = [...presenterVideos];
		// your thumbnail should always be first
		if (host) {
			const producerVideoIndex = newVideos.findIndex((vid) => vid.self);
			// Check if host thumbnail is not first (or -1 which is not found)
			if (producerVideoIndex > 0) {
				newVideos.unshift(newVideos.splice(producerVideoIndex, 1)[0]);
			}
		}
		return newVideos;
	}, [host, presenterVideos]);

	const renderedSrtFeeds = useMemo(
		() => (
			roomState === ROOM_STATES.EDITING && host
				? standaloneSrtStreams
				: standaloneSrtStreams.filter((s) => s.status === SrtStreamStatus.active)
		),
		[host, roomState, standaloneSrtStreams],
	);

	const { attachMask } = useEditProfileMask({});
	const dropCallback = async (item: DragItem, dropData: any) => {
		const maskId = Number(item?.id);
		const email = dropData?.id;
		if (maskId && email && !Number.isNaN(maskId)) {
			await attachMask(email, maskId);
		}
	};

	return (
		<>	{useSip && <SipConnector></SipConnector>}
			<div
				className="restricted-feeds-tooltip"
				id="feeds-restricted-tooltip-background"
				ref={feedsRef}
			/>
			<div id="portal-onsite-feed-menu" />
			<div className={classNames('video-thumbs feeds-tab', className ?? '')} style={{ display: 'grid' }}>

				{/* only show all presenter videos when connected to OpenTok */}
				{opentok && <>
					{opentok?.session?.connection
						&& renderedPresenterVideos.map((vid: VideoStream, i: number) => <VideoFeedThumbnail
							video={vid}
							key={i}
							socket={socket}
							feedsRef={feedsRef}
							opentok={opentok}
							dropCallback={dropCallback}
						/>)}

					{/* show your own video when first connecting to OpenTok */}
					{!opentok?.session?.connection && restrictedUserState === RESTRICTED_USER_STATES.NONE
						&& <>
							{preconnectVideo && <VideoFeedThumbnail
								video={preconnectVideo}
								socket={socket}
								feedsRef={feedsRef}
							/>}
						</>
					}
				</>}

				{restrictedUsers.map((rUser: ConnectedUser, i: number) => <RestrictedUserThumbnail
					key={i}
					restrictedUser={rUser}
					feedsRef={feedsRef}
				/>)}

				{invitedUsersNotInRoom.map((invite: Invitation, i: number) => <VideoFeedFakeThumbnail
					invite={invite}
					key={i}
					dropCallback={dropCallback}
				/>)}

				{useSrtStreams && renderedSrtFeeds.map((srtStream: SrtStream, i: number) => (
					<SrtStreamThumbnail
						key={i}
						srtStream={srtStream}
						location={ThumbnailLocation.FeedsTab}
					/>
				))}

				{onsiteLocations && onsiteLocations.map(
					(onsiteLocation: OnsiteLocation, i: number) => {
						const locationStreams = onsiteLocationStreams.get(onsiteLocation.id);
						if (locationStreams?.length) {
							return locationStreams.map((locationStream, j) => (
								<OnsiteLocationThumbnail
									key={`${i}-${j}`}
									onsiteLocation={onsiteLocation}
									location={ThumbnailLocation.FeedsTab}
									locationStream={locationStream}
								/>
							));
						}
						const srtStream = onsiteSrtStreams.find(
							(s) => s.status === SrtStreamStatus.active && s.id === onsiteLocation.srtId,
						);
						if (srtStream) {
							return (
								<OnsiteLocationThumbnail
									key={i}
									onsiteLocation={onsiteLocation}
									location={ThumbnailLocation.FeedsTab}
									srtStream={srtStream}
								/>
							);
						}
						return (
							roomState === ROOM_STATES.EDITING && host ? (
								<OnsiteLocationThumbnail
									key={i}
									onsiteLocation={onsiteLocation}
									location={ThumbnailLocation.FeedsTab}
								/>
							) : (
								<></>
							)
						);
					},
				)}

				{(roomState === ROOM_STATES.SHOWTIME) && host && (
					<button className='video-thumb transparent border-dashed height-full' onClick={() => { invitePresenters(); }}>
						<div className='video-wrapper'></div>
						<Icon name={ICONS.ADD} size={14} color={COLORS.PALE_GREEN} className='margin-right-small' />
						<span className='text-green font-size-14 font-weight-heavy'>Invite</span>
					</button>
				)}

			</div>
		</>
	);
}
